import React, { useEffect, useState } from 'react';
import { Button, Form, Header, Icon, Message, Table } from 'semantic-ui-react';
import { API } from '../helpers';
import BillingDateCalendarV89, {
  serializeDateIntervalsV89,
} from './BillingDateCalendarV89';
import useBillingDateSelectionV97 from '../pages/Billing/BillingDateSelectionStateV97';
import { formatImagePixelsV97 } from './imagePixelsV97';
import { RetryOutcomeV1021, RetryRouteMetadataV1021 } from './retryAuditV1021';
import { numberRetryAttemptsV111 } from './retryPresentationV111';

const labels = {
  all: '空转 / 计费异常',
  dispatched_unknown: '已发出，未确认有效产出',
  accepted_undelivered: '有效产出，未确认写完',
  delivered_billing_anomaly: '应用写出完成，计费异常',
  selected: '选中渠道',
  candidate_selected: '选中候选',
  attempt_started: '开始尝试',
  dispatched: '已发往上游',
  provider_response: '上游响应',
  output_accepted: '确认有效产出',
  image_output_warning: '图片规格警告',
  response_delivered: '应用响应写出完成',
  client_delivery_rejected: '最终响应非成功状态',
  billing_anomaly: '计费异常',
  local_rejection: '本地拒绝，未发出',
  attempt_error: '尝试出错',
  attempt_finished: '尝试结束',
  fallback: '切换重试目标',
  skipped: '跳过候选',
  canceled: '请求取消',
};

export const auditLabelV97 = (value) => labels[value] || value || '-';
export const retryRouteOutcomeV1021 = (route) => {
  const meta = route?.retry_v1021;
  if (!['request', 'same_source_retry', 'fallback_retry'].includes(meta?.kind) ||
      !['language', 'image', 'video', 'other'].includes(meta?.category) || !(meta?.ordinal > 0)) return 'unknown';
  if (route.canceled && !route.delivered) return 'canceled';
  if (!route.finalized) return 'pending';
  if (!route.dispatched) return 'not_dispatched';
  return `${meta.kind}_${route.delivered ? 'success' : 'failure'}`;
};
export const auditRiskV97 = (route) => {
  if (!route?.dispatched) return '';
  if (route.delivered)
    return route.billing_error_code ? 'delivered_billing_anomaly' : '';
  return route.accepted ? 'accepted_undelivered' : 'dispatched_unknown';
};
export const auditCostV97 = (cost) => {
  if (cost?.status === 'not_dispatched') return '未发出';
  if (cost?.status === 'provider_reported' &&
      cost.cost_basis === 'openrouter_account_charge') {
    const amount = cost.amount_usd_v1015;
    return typeof amount === 'string' && /^(0|[1-9]\d{0,11})(\.\d{1,36})?$/.test(amount)
      ? `USD ${amount} (OpenRouter 账户扣费回报)`
      : '未知';
  }
  if (
    !['recorded_cost', 'actual_receipt'].includes(cost?.status) ||
    cost.amount_nano_usd == null
  )
    return '未知';
  if (
    typeof cost.amount_nano_usd === 'number' &&
    !Number.isSafeInteger(cost.amount_nano_usd)
  )
    return '未知';
  const amount = String(cost.amount_nano_usd);
  if (!/^\d+$/.test(amount)) return '未知';
  const padded = amount.padStart(10, '0');
  const whole = padded.slice(0, -9).replace(/^0+(?=\d)/, '');
  const fraction = padded.slice(-9).replace(/0+$/, '');
  return `USD ${whole}${fraction ? `.${fraction}` : ''} (${
    cost.status === 'actual_receipt' ? '实际回执成本' : '记录成本'
  })`;
};
const dateText = (value) =>
  value > 0 ? new Date(value).toLocaleString() : '-';

export const formatResponseWriteV101 = (observation) => {
  if (
    observation?.measurement_layer !== 'image_adapter_body' ||
    !Number.isSafeInteger(observation.expected_bytes) ||
    !Number.isSafeInteger(observation.written_bytes) ||
    observation.expected_bytes < 0 ||
    observation.written_bytes < 0 ||
    observation.written_bytes > observation.expected_bytes ||
    typeof observation.write_failed !== 'boolean'
  )
    return '未采集 / 未知';
  return `预计 ${observation.expected_bytes} B；写入 ${
    observation.written_bytes
  } B；${observation.write_failed ? '写入返回错误' : '写入未报错'}`;
};

export const formatImageOutputSpecV912 = (spec) => {
  if (!spec || typeof spec !== 'object' || Array.isArray(spec)) return '未知';
  if (spec.status != null && spec.status !== 'measured') return '未知';
  const positivePixels = (value) =>
    Number.isSafeInteger(value) && value > 0 && value <= 1000000;
  const parts = [];
  if (positivePixels(spec.width) && positivePixels(spec.height)) {
    parts.push(`${spec.width} x ${spec.height} px`);
  }
  if (typeof spec.tier === 'string' && /^\d+(?:\.\d+)?K$/i.test(spec.tier)) {
    parts.push(`档位 ${spec.tier}`);
  }
  if (typeof spec.aspect === 'string' && /^\d+:\d+$/.test(spec.aspect)) {
    parts.push(`宽高比 ${spec.aspect}`);
  }
  if (positivePixels(spec.long_edge)) {
    parts.push(`长边 ${spec.long_edge} px`);
  }
  return parts.join('；') || '未知';
};

export const ImageOutputWarningsV912 = ({ warnings }) =>
  Array.isArray(warnings)
    ? warnings
        .filter((warning) => warning?.code === 'image_output_size_mismatch')
        .map((warning, index) => (
          <Message
            warning
            size='small'
            role='status'
            key={index}
            data-image-output-warning-v912={warning.code}
          >
            <Message.Header>图片规格偏差（非交付失败）</Message.Header>
            <div>请求：{formatImageOutputSpecV912(warning.requested)}</div>
            <div>实际：{formatImageOutputSpecV912(warning.actual)}</div>
            <p>
              有效图片仍正常交付并按配置计费；交付与计费状态以各自记录为准。
            </p>
          </Message>
        ))
    : null;

export const RequestAuditTraceV97 = ({ data }) => {
  if (!data?.request) return null;
  const audit = data.request;
  const retryNumbers = numberRetryAttemptsV111(
    (audit.trace?.routes || []).map((route) => ({
      ...route.retry_v1021,
      dispatched: route.dispatched === true,
    })),
    audit.trace?.truncated === true
  );
  const costs = new Map(
    (data.costs || []).map((cost) => [cost.route_no, cost])
  );
  return (
    <section aria-label='请求路由审计'>
      <Header as='h4'>请求路由审计</Header>
      <p>
        应用响应：{audit.delivered ? '写出完成' : '未确认写完'}； 最终响应
        HTTP：
        {audit.client_http_status || '-'}
      </p>
      <p>客户端收齐：未知（未采集回执）</p>
      {data.cost_evidence_status === 'unavailable' && (
        <Message warning content='供应商成本证据暂不可用' />
      )}
      {audit.trace?.truncated && (
        <Message warning content='事件数量超过上限，序列不完整' />
      )}
      <div style={{ overflowX: 'auto' }}>
        <Table compact celled unstackable style={{ minWidth: 720 }}>
          <Table.Header>
            <Table.Row>
              {[
                '路由 / 尝试',
                '渠道',
                'Source / 重试',
                '处理结果',
                '供应商成本',
                '上游原图像素',
                '证据',
              ].map((title) => (
                <Table.HeaderCell key={title}>{title}</Table.HeaderCell>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {(audit.trace?.routes || []).map((route, index) => {
              const cost = costs.get(route.route_no);
              return (
                <Table.Row key={route.route_no}>
                  <Table.Cell>
                    {route.route_no} / {route.attempt_no || '-'}
                  </Table.Cell>
                  <Table.Cell>
                    #{route.channel_id} {route.channel_name}
                  </Table.Cell>
                  <Table.Cell style={{ overflowWrap: 'anywhere' }}>
                    <RetryRouteMetadataV1021 value={{ ...route.retry_v1021,
                      retry_number_v111: retryNumbers[index]?.retry_number_v111 }} />
                  </Table.Cell>
                  <Table.Cell>
                    <div><RetryOutcomeV1021 outcome={retryRouteOutcomeV1021(route)} /></div>
                    {auditLabelV97(auditRiskV97(route)) === '-'
                      ? route.delivered
                        ? '应用写出完成'
                        : '未发出'
                      : auditLabelV97(auditRiskV97(route))}
                    {route.error_code && <div>{route.error_code}</div>}
                    {route.billing_error_code && (
                      <div>{route.billing_error_code}</div>
                    )}
                  </Table.Cell>
                  <Table.Cell>{auditCostV97(cost)}</Table.Cell>
                  <Table.Cell
                    style={{ overflowWrap: 'anywhere', minWidth: 150 }}
                  >
                    {formatImagePixelsV97(route.image_dimensions_v97, '未知')}
                    <ImageOutputWarningsV912
                      warnings={route.image_output_warnings_v912}
                    />
                  </Table.Cell>
                  <Table.Cell style={{ overflowWrap: 'anywhere' }}>
                    {route.openrouter_v1015 && (
                      <div data-openrouter-evidence-v1015>
                        <div>OpenRouter 模型：{route.openrouter_v1015.model || '未知'}</div>
                        <div>供应商：{route.openrouter_v1015.provider || '未知'}</div>
                        <div>服务档位：{route.openrouter_v1015.service_tier || '未知'}</div>
                        <div>生成 ID：{route.openrouter_v1015.generation_id || '未知'}</div>
                        <div>BYOK：{typeof route.openrouter_v1015.is_byok === 'boolean'
                          ? String(route.openrouter_v1015.is_byok) : '未知'}</div>
                        <div>费用证据：{route.openrouter_v1015.cost_status || 'missing'}</div>
                      </div>
                    )}
                    <div>
                      正文写入（Images 适配器）：
                      {formatResponseWriteV101(route.response_write_v101)}
                    </div>
                    {cost?.evidence_source
                      ? `${cost.evidence_source} #${cost.evidence_id}`
                      : '-'}
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <Table compact celled unstackable style={{ minWidth: 720 }}>
          <Table.Header>
            <Table.Row>
              {['顺序', '时间', '渠道', '事件', 'HTTP / 原因'].map((title) => (
                <Table.HeaderCell key={title}>{title}</Table.HeaderCell>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {(audit.trace?.events || []).map((event) => (
              <Table.Row key={event.sequence}>
                <Table.Cell>{event.sequence}</Table.Cell>
                <Table.Cell>{dateText(event.at_ms)}</Table.Cell>
                <Table.Cell>
                  #{event.channel_id} {event.channel_name}
                </Table.Cell>
                <Table.Cell>{auditLabelV97(event.phase)}</Table.Cell>
                <Table.Cell style={{ overflowWrap: 'anywhere' }}>
                  {[event.http_status || '', event.code || '']
                    .filter(Boolean)
                    .join(' / ') || '-'}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>
    </section>
  );
};

export default function RequestAuditV97({
  root = false,
  customerScope = 'ordinary',
}) {
  const [risk, setRisk] = useState('all');
  const [requestID, setRequestID] = useState('');
  const [modelName, setModelName] = useState('');
  const [query, setQuery] = useState(null);
  const [page, setPage] = useState(0);
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState({
    loading: true,
    items: [],
    error: '',
    more: false,
  });
  const [selected, setSelected] = useState(null);
  const { dates, setDates, refreshToday } = useBillingDateSelectionV97(
    `request-audit.v97.${customerScope}`
  );
  const intervals = serializeDateIntervalsV89(dates);
  const activeIntervals = query?.date_intervals ?? intervals;

  useEffect(() => {
    if (!root || !activeIntervals) return;
    let active = true;
    const controller = new AbortController();
    setState({ loading: true, items: [], error: '', more: false });
    setSelected(null);
    API.get('/api/log/request_audit_v97', {
      signal: controller.signal,
      params: {
        customer_scope: customerScope,
        risk,
        p: page,
        page_size: 25,
        date_intervals: activeIntervals,
        request_id: query?.request_id || '',
        model_name: query?.model_name || '',
      },
    })
      .then((response) => {
        if (!active) return;
        if (!response?.data?.success)
          throw new Error('request audit unavailable');
        setState({
          loading: false,
          items: response.data.data?.items || [],
          more: response.data.data?.has_more === true,
          error: '',
        });
      })
      .catch(() => {
        if (active)
          setState({
            loading: false,
            items: [],
            more: false,
            error: '请求审计暂不可用；未知不代表没有空转请求',
          });
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [root, customerScope, risk, page, activeIntervals, query, revision]);

  if (!root) return null;
  return (
    <section aria-label='空转与计费异常'>
      <Header as='h3'>空转与计费异常</Header>
      <Message content='仅含 V9.7 启用后记录的请求。发出请求、超时或 HTTP 200 不等于已扣费；供应商成本以记录证据为准。' />
      <BillingDateCalendarV89
        value={dates}
        onChange={setDates}
        onRefreshToday={refreshToday}
        compact
        eligibilityEndpoint=''
      />
      <Form
        onSubmit={() => {
          setPage(0);
          setQuery({
            date_intervals: intervals,
            request_id: requestID.trim(),
            model_name: modelName.trim(),
          });
          setRevision((value) => value + 1);
        }}
      >
        <Form.Group widths='equal'>
          <Form.Dropdown
            selection
            label='审计类型'
            value={risk}
            options={[
              'all',
              'accepted_undelivered',
              'dispatched_unknown',
              'delivered_billing_anomaly',
            ].map((value) => ({
              key: value,
              value,
              text: auditLabelV97(value),
            }))}
            onChange={(event, { value }) => {
              setRisk(value);
              setPage(0);
            }}
          />
          <Form.Input
            label='请求 ID'
            value={requestID}
            onChange={(event, { value }) => setRequestID(value)}
          />
          <Form.Input
            label='模型'
            value={modelName}
            onChange={(event, { value }) => setModelName(value)}
          />
        </Form.Group>
        <Button
          type='submit'
          icon
          labelPosition='left'
          disabled={!intervals}
          loading={state.loading}
        >
          <Icon name='search' />
          查询
        </Button>
      </Form>
      {state.error && <Message warning content={state.error} />}
      {!state.loading && !state.error && state.items.length === 0 && (
        <Message content='所选范围内没有已记录的匹配请求' />
      )}
      <div style={{ overflowX: 'auto', marginTop: 12 }}>
        <Table compact celled unstackable style={{ minWidth: 720 }}>
          <Table.Header>
            <Table.Row>
              {['请求 ID', '时间 / 模型', '应用响应', '异常尝试', '详情'].map(
                (title) => (
                  <Table.HeaderCell key={title}>{title}</Table.HeaderCell>
                )
              )}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {state.items.map((entry) => (
              <Table.Row key={entry.request.request_id}>
                <Table.Cell style={{ overflowWrap: 'anywhere' }}>
                  {entry.request.request_id}
                </Table.Cell>
                <Table.Cell>
                  {dateText(entry.request.started_at * 1000)}
                  <br />
                  {entry.request.model_name}
                </Table.Cell>
                <Table.Cell>
                  {entry.request.delivered ? '应用写出完成' : '未确认写完'} /{' '}
                  {entry.request.client_http_status || '-'}
                </Table.Cell>
                <Table.Cell>
                  {(entry.request.trace?.routes || [])
                    .filter((route) => auditRiskV97(route))
                    .map((route) => (
                      <div key={route.route_no}>
                        #{route.channel_id} {auditLabelV97(auditRiskV97(route))}
                      </div>
                    ))}
                </Table.Cell>
                <Table.Cell>
                  <Button
                    basic
                    icon='list ordered'
                    title='查看完整路由序列'
                    aria-label='查看完整路由序列'
                    onClick={() => setSelected(entry)}
                  />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>
      <Button
        basic
        icon='chevron left'
        title='上一页'
        aria-label='上一页'
        disabled={page === 0 || state.loading}
        onClick={() => setPage((value) => value - 1)}
      />
      <span>{page + 1}</span>
      <Button
        basic
        icon='chevron right'
        title='下一页'
        aria-label='下一页'
        disabled={!state.more || state.loading}
        onClick={() => setPage((value) => value + 1)}
      />
      {selected && <RequestAuditTraceV97 data={selected} />}
    </section>
  );
}
